import { beatById } from '../data/narrative-atlas';
import { photoByKey, publicPhotoPath } from '../renderers/archive-act';
import { localeText } from '../locale';

export interface SegmentSpec {
  id: string;
  index: string;
  title: string;
  era: string;
  photoKey?: string;
}

export interface PhotoSpec {
  key: string;
  alt: string;
  caption: string;
}

export interface StoryGalleryItem {
  year?: string;
  title: string;
  detail?: string;
  photo: PhotoSpec;
  narrativeTarget?: string;
}

export interface MapPhotoStitch {
  date: string;
  place: string;
  photo: PhotoSpec;
}

export interface TimelineRow {
  year: string;
  title: string;
  detail: string;
}

export interface FactRow {
  label: string;
  value: string;
  note?: string;
}

export interface MeasureFieldSpec {
  value: string;
  unit: string;
  kicker: string;
  text: string;
  tone?: 'sage' | 'blue' | 'ochre' | 'clay';
}

export interface DocumentFolioSpec {
  mark: string;
  kicker: string;
  title: string;
  place: string;
  year: string;
  note: string;
  covers?: readonly {
    src: string;
    alt: string;
    volume: string;
    sourceUrl: string;
    sourceLabel: string;
    width: number;
    height: number;
  }[];
}

export interface PlaceDossierItem {
  title: string;
  status: string;
  text: string;
}

export interface EditorialColumnsSpec {
  eyebrow?: string;
  title?: string;
  lead?: string;
  paragraphs: string[];
  photo?: PhotoSpec;
}

export interface PortraitPairPerson {
  name: string;
  relation: string;
  text: string;
  photo: PhotoSpec;
}

export interface PortraitPairSpec {
  eyebrow: string;
  title: string;
  lead: string;
  people: [PortraitPairPerson, PortraitPairPerson];
  familyPhoto?: PhotoSpec;
}


export interface ChapterHeaderSpec {
  index: string;
  era: string;
  title: string;
  lead: string;
  photo?: PhotoSpec;
}

function element<K extends keyof HTMLElementTagNameMap>(tag: K, className = '', text = ''): HTMLElementTagNameMap[K] {
  const node = document.createElement(tag);
  if (className) node.className = className;
  if (text) node.textContent = text;
  return node;
}

function photoImage(spec: PhotoSpec, eager = false): HTMLImageElement | null {
  const photo = photoByKey.get(spec.key);
  const variant = photo?.v.find(({ width }) => width >= 1280) ?? photo?.v[photo.v.length - 1];
  if (!photo || !variant) return null;

  const image = element('img');
  image.src = publicPhotoPath(variant.src);
  image.srcset = photo.v.map((item) => `${publicPhotoPath(item.src)} ${item.width}w`).join(', ');
  image.sizes = '(max-width: 760px) 100vw, 68vw';
  image.width = variant.width;
  image.height = variant.height;
  image.alt = spec.alt;
  image.loading = eager ? 'eager' : 'lazy';
  image.decoding = 'async';
  image.fetchPriority = eager ? 'high' : 'auto';
  return image;
}

function configureGalleryOpener(
  opener: HTMLElement,
  spec: PhotoSpec,
  narrativeTarget?: string,
): boolean {
  const photo = photoByKey.get(spec.key);
  const fullVariant = photo?.v[photo.v.length - 1];
  if (!photo || !fullVariant) return false;

  opener.dataset.idsGalleryOpen = '';
  opener.dataset.idsPhotoKey = spec.key;
  opener.dataset.idsCaption = spec.caption;
  opener.dataset.pswpWidth = String(fullVariant.width);
  opener.dataset.pswpHeight = String(fullVariant.height);
  opener.dataset.pswpSrc = publicPhotoPath(fullVariant.src);
  opener.dataset.pswpSrcset = photo.v
    .map((item) => `${publicPhotoPath(item.src)} ${item.width}w`)
    .join(', ');
  if (narrativeTarget) opener.dataset.idsNarrativeTarget = narrativeTarget;
  opener.setAttribute('aria-haspopup', 'dialog');
  opener.setAttribute(
    'aria-label',
    localeText(
      `Открыть фотографию: ${spec.caption || spec.alt}`,
      `Сүрөттү ачуу: ${spec.caption || spec.alt}`,
    ),
  );
  if (opener instanceof HTMLAnchorElement) opener.href = publicPhotoPath(fullVariant.src);
  return true;
}

export function photoFigure(spec: PhotoSpec, className = '', eager = false): HTMLElement {
  const figure = element('figure', `ep-photo ${className}`.trim());
  const image = photoImage(spec, eager);
  if (!image) {
    figure.append(element('p', 'ep-photo__missing', localeText(`Фотография ${spec.key} не найдена`, `${spec.key} сүрөтү табылган жок`)));
    return figure;
  }

  const trigger = element('a', 'ids-gallery__item');
  if (!configureGalleryOpener(trigger, spec)) {
    figure.append(element('p', 'ep-photo__missing', localeText(`Фотография ${spec.key} не найдена`, `${spec.key} сүрөтү табылган жок`)));
    return figure;
  }
  trigger.append(image);

  const caption = element('figcaption');
  caption.append(element('span', '', spec.caption));
  figure.dataset.photoKey = spec.key;
  figure.append(trigger, caption);
  return figure;
}

let segmentRailSequence = 0;
const openSegmentMenus = new Set<HTMLDialogElement>();

function setSegmentMenuScrollLock(dialog: HTMLDialogElement, open: boolean): void {
  if (open) openSegmentMenus.add(dialog);
  else openSegmentMenus.delete(dialog);
  const locked = openSegmentMenus.size > 0;
  document.documentElement.classList.toggle('ep-segments-menu-open', locked);
  document.body.classList.toggle('ep-segments-menu-open', locked);
}

function setSegmentLinkCurrent(link: HTMLAnchorElement, current: boolean): void {
  if (current) link.setAttribute('aria-current', 'location');
  else link.removeAttribute('aria-current');

  link.closest<HTMLElement>('.ep-segments__item')?.classList.toggle('is-current', current);
  const status = link.querySelector<HTMLElement>('.ep-segments__card-status');
  if (status) status.hidden = !current;
}

function updateSegmentRailState(nav: HTMLElement, id: string): void {
  const links = [...nav.querySelectorAll<HTMLAnchorElement>('[data-segment-target]')];
  let activeLink: HTMLAnchorElement | undefined;

  for (const link of links) {
    const current = link.dataset.segmentTarget === id;
    setSegmentLinkCurrent(link, current);
    if (current) activeLink = link;
  }
  if (!activeLink) return;

  const segmentIndex = activeLink.dataset.segmentIndex ?? '';
  const segmentTitle = activeLink.dataset.segmentTitle ?? '';
  const segmentEra = activeLink.dataset.segmentEra ?? '';
  const position = Number(activeLink.dataset.segmentPosition) || links.indexOf(activeLink) + 1;
  const total = links.length;

  nav.dataset.activeSegment = id;
  nav.dataset.activeIndex = segmentIndex;
  nav.dataset.activePosition = String(position);
  nav.style.setProperty('--ep-segments-progress', total ? String(position / total) : '0');

  const current = nav.querySelector<HTMLElement>('.ep-segments__current');
  const number = nav.querySelector<HTMLElement>('.ep-segments__current-number');
  const title = nav.querySelector<HTMLElement>('.ep-segments__current-title');
  const era = nav.querySelector<HTMLElement>('.ep-segments__current-era');
  if (number) number.textContent = segmentIndex;
  if (title) title.textContent = segmentTitle;
  if (era) era.textContent = segmentEra;
  current?.setAttribute(
    'aria-label',
    localeText(
      `Глава ${segmentIndex}: ${segmentTitle}${segmentEra ? `. ${segmentEra}` : ''}`,
      `Бөлүм ${segmentIndex}: ${segmentTitle}${segmentEra ? `. ${segmentEra}` : ''}`,
    ),
  );

  const progress = nav.querySelector<HTMLElement>('.ep-segments__progress');
  progress?.setAttribute('aria-valuenow', String(position));
  progress?.setAttribute('aria-valuemax', String(total));
  progress?.setAttribute('aria-valuetext', localeText(`Глава ${position} из ${total}: ${segmentTitle}`, `Бөлүм ${position} / ${total}: ${segmentTitle}`));
  const count = nav.querySelector<HTMLElement>('.ep-segments__progress-count');
  if (count) count.textContent = `${position} / ${total}`;
}

export function createSegmentRail(segments: SegmentSpec[], label = localeText('Главы биографии', 'Өмүр баян бөлүмдөрү')): HTMLElement {
  const railId = ++segmentRailSequence;
  const dialogId = `ep-segments-menu-${railId}`;
  const dialogTitleId = `${dialogId}-title`;
  const dialogDescriptionId = `${dialogId}-description`;
  const initialSegment = segments[0];

  const nav = element('nav', 'ep-segments');
  nav.setAttribute('aria-label', label);
  nav.dataset.segmentCount = String(segments.length);
  nav.dataset.menuState = 'closed';

  const bar = element('div', 'ep-segments__bar');
  const current = element('div', 'ep-segments__current');
  current.setAttribute('aria-live', 'polite');
  current.setAttribute('aria-atomic', 'true');
  const folio = element('span', 'ep-segments__folio');
  folio.append(
    element('span', 'ep-segments__folio-label', localeText('Глава', 'Бөлүм')),
    element('span', 'ep-segments__current-number', initialSegment?.index ?? '—'),
  );
  const currentCopy = element('span', 'ep-segments__current-copy');
  currentCopy.append(
    element('strong', 'ep-segments__current-title', initialSegment?.title ?? label),
    element('small', 'ep-segments__current-era', initialSegment?.era ?? ''),
  );
  current.append(folio, currentCopy);

  const menuTrigger = element('button', 'ep-segments__menu-trigger');
  menuTrigger.type = 'button';
  menuTrigger.setAttribute('aria-haspopup', 'dialog');
  menuTrigger.setAttribute('aria-controls', dialogId);
  menuTrigger.setAttribute('aria-expanded', 'false');
  menuTrigger.setAttribute('aria-label', localeText('Открыть оглавление', 'Мазмунду ачуу'));
  const triggerCopy = element('span', 'ep-segments__menu-copy');
  triggerCopy.append(
    element('small', '', localeText('Все главы', 'Бардык бөлүмдөр')),
    element('strong', '', localeText('Оглавление', 'Мазмун')),
  );
  const triggerMark = element('span', 'ep-segments__menu-mark');
  triggerMark.setAttribute('aria-hidden', 'true');
  triggerMark.append(element('span'), element('span'));
  menuTrigger.append(triggerCopy, triggerMark);

  const progress = element('div', 'ep-segments__progress');
  progress.setAttribute('role', 'progressbar');
  progress.setAttribute('aria-label', localeText('Прогресс по главам', 'Бөлүмдөр боюнча жылыш'));
  progress.setAttribute('aria-valuemin', initialSegment ? '1' : '0');
  progress.setAttribute('aria-valuemax', String(segments.length));
  progress.setAttribute('aria-valuenow', initialSegment ? '1' : '0');
  const progressTrack = element('span', 'ep-segments__progress-track');
  progressTrack.setAttribute('aria-hidden', 'true');
  progressTrack.append(element('span', 'ep-segments__progress-fill'));
  progress.append(
    element('span', 'ep-segments__progress-label', localeText('Прогресс', 'Жылыш')),
    progressTrack,
    element('span', 'ep-segments__progress-count', initialSegment ? `1 / ${segments.length}` : '0 / 0'),
  );
  bar.append(current, menuTrigger, progress);

  const dialog = element('dialog', 'ep-segments__dialog');
  dialog.id = dialogId;
  dialog.setAttribute('aria-labelledby', dialogTitleId);
  dialog.setAttribute('aria-describedby', dialogDescriptionId);
  dialog.setAttribute('aria-modal', 'true');
  const dialogShell = element('div', 'ep-segments__dialog-shell');
  const dialogHeader = element('header', 'ep-segments__dialog-header');
  const dialogHeading = element('div', 'ep-segments__dialog-heading');
  dialogHeading.append(
    element('p', 'ep-segments__dialog-kicker', localeText('Маршрут жизни', 'Өмүр жолу')),
  );
  const dialogTitle = element('h2', '', label);
  dialogTitle.id = dialogTitleId;
  const dialogDescription = element(
    'p',
    'ep-segments__dialog-description',
    localeText(`${segments.length} глав · листайте фотографии и выбирайте место в истории`, `${segments.length} бөлүм · сүрөттөрдү жылдырып, баяндагы жерди тандаңыз`),
  );
  dialogDescription.id = dialogDescriptionId;
  dialogHeading.append(dialogTitle, dialogDescription);

  const closeButton = element('button', 'ep-segments__close');
  closeButton.type = 'button';
  closeButton.setAttribute('aria-label', localeText('Закрыть оглавление', 'Мазмунду жабуу'));
  closeButton.append(
    element('span', 'ep-segments__close-label', localeText('Закрыть', 'Жабуу')),
    element('span', 'ep-segments__close-mark', '×'),
  );
  closeButton.lastElementChild?.setAttribute('aria-hidden', 'true');
  dialogHeader.append(dialogHeading, closeButton);

  const list = element('ol', 'ep-segments__list');
  list.setAttribute('aria-label', label);
  for (const [position, segment] of segments.entries()) {
    const item = element('li', 'ep-segments__item');
    const link = element('a', 'ep-segments__link');
    link.href = `#${segment.id}`;
    link.dataset.segmentTarget = segment.id;
    link.dataset.segmentIndex = segment.index;
    link.dataset.segmentTitle = segment.title;
    link.dataset.segmentEra = segment.era;
    link.dataset.segmentPosition = String(position + 1);

    const media = element('span', 'ep-segments__media');
    media.setAttribute('aria-hidden', 'true');
    const photo = segment.photoKey ? photoByKey.get(segment.photoKey) : undefined;
    const variant = photo?.v.find(({ width }) => width >= 720) ?? photo?.v[photo.v.length - 1];
    if (photo && variant) {
      const image = element('img');
      image.src = publicPhotoPath(variant.src);
      image.srcset = photo.v.map((item) => `${publicPhotoPath(item.src)} ${item.width}w`).join(', ');
      image.sizes = '(max-width: 700px) 38vw, (max-width: 1199px) 30vw, 260px';
      image.width = variant.width;
      image.height = variant.height;
      image.alt = '';
      image.loading = 'lazy';
      image.decoding = 'async';
      media.append(image);
    } else {
      media.classList.add('is-photo-missing');
    }
    media.append(element('span', 'ep-segments__media-number', segment.index));

    const copy = element('span', 'ep-segments__card-copy');
    const status = element('span', 'ep-segments__card-status', localeText('Текущая глава', 'Учурдагы бөлүм'));
    status.hidden = true;
    copy.append(
      element('span', 'ep-segments__card-kicker', localeText(`Глава ${segment.index}`, `Бөлүм ${segment.index}`)),
      element('strong', 'ep-segments__card-title', segment.title),
      element('small', 'ep-segments__card-era', segment.era),
      status,
    );
    link.append(media, copy);
    item.append(link);
    list.append(item);
  }

  dialogShell.append(dialogHeader, list);
  dialog.append(dialogShell);
  nav.append(bar, dialog);

  const returnFocus = menuTrigger;
  let pointerStartedOnBackdrop = false;

  const syncMenuState = (open: boolean): void => {
    nav.classList.toggle('is-menu-open', open);
    nav.dataset.menuState = open ? 'open' : 'closed';
    menuTrigger.setAttribute('aria-expanded', String(open));
    setSegmentMenuScrollLock(dialog, open);
  };

  const closeMenu = (returnValue = ''): void => {
    if (dialog.open) dialog.close(returnValue);
  };

  menuTrigger.addEventListener('click', () => {
    if (dialog.open) return;
    dialog.showModal();
    syncMenuState(true);

    requestAnimationFrame(() => {
      if (!dialog.open) return;
      closeButton.focus({ preventScroll: true });
      dialog
        .querySelector<HTMLAnchorElement>('[aria-current="location"]')
        ?.closest<HTMLElement>('.ep-segments__item')
        ?.scrollIntoView({ block: 'nearest', inline: 'nearest' });
    });
  });

  closeButton.addEventListener('click', () => closeMenu('close'));
  dialog.addEventListener('close', () => {
    syncMenuState(false);
    if (dialog.returnValue !== 'navigate' && returnFocus.isConnected) {
      requestAnimationFrame(() => {
        if (!dialog.open) returnFocus.focus({ preventScroll: true });
      });
    }
  });
  dialog.addEventListener('pointerdown', (event) => {
    pointerStartedOnBackdrop = event.target === dialog;
  });
  dialog.addEventListener('pointerup', (event) => {
    if (pointerStartedOnBackdrop && event.target === dialog) closeMenu('backdrop');
    pointerStartedOnBackdrop = false;
  });
  dialog.addEventListener('pointercancel', () => {
    pointerStartedOnBackdrop = false;
  });

  for (const link of list.querySelectorAll<HTMLAnchorElement>('[data-segment-target]')) {
    link.addEventListener('click', (event) => {
      if (event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
      const target = link.dataset.segmentTarget;
      const targetElement = target ? document.getElementById(target) : null;
      if (!target || !targetElement) return;

      event.preventDefault();
      updateSegmentRailState(nav, target);
      syncMenuState(false);
      closeMenu('navigate');

      if (window.location.hash === link.hash) window.history.replaceState(null, '', link.hash);
      else window.history.pushState(null, '', link.hash);

      const rootScrollBehavior = document.documentElement.style.scrollBehavior;
      document.documentElement.style.scrollBehavior = 'auto';
      targetElement.scrollIntoView({ block: 'start' });
      document.documentElement.style.scrollBehavior = rootScrollBehavior;

      const heading = targetElement.querySelector<HTMLElement>('h2, h3');
      if (heading) {
        heading.tabIndex = -1;
        heading.focus({ preventScroll: true });
      }
    });
  }

  if (initialSegment) updateSegmentRailState(nav, initialSegment.id);
  else menuTrigger.disabled = true;
  return nav;
}

export function createBiographyIntro(spec: {
  eyebrow: string;
  kicker?: string;
  title: string;
  lead: string;
  photo?: PhotoSpec;
  beatId?: string;
  placeLabel?: string;
}): HTMLElement {
  const section = element('section', spec.beatId ? 'ep-intro ep-map-stage' : 'ep-intro');
  if (spec.beatId) section.dataset.beatId = spec.beatId;
  if (!spec.kicker && !spec.lead) section.classList.add('is-minimal');
  const portrait = spec.photo ? photoFigure(spec.photo, 'ep-intro__portrait', true) : null;
  if (!portrait) section.classList.add('is-text-only');
  const wash = element('div', 'ep-intro__wash');
  wash.setAttribute('aria-hidden', 'true');
  const copy = element('div', 'ep-intro__copy');
  if (spec.placeLabel) copy.append(element('p', 'ep-intro__place', spec.placeLabel));
  if (spec.kicker) copy.append(element('p', 'ep-intro__kicker', spec.kicker));
  copy.append(
    element('p', 'ep-intro__meta', spec.eyebrow),
    element('h1', '', spec.title),
  );
  if (spec.lead) copy.append(element('p', 'ep-intro__lead', spec.lead));
  if (spec.photo?.caption) copy.append(element('p', 'ep-intro__credit', spec.photo.caption));

  const yearParts = spec.eyebrow.split('—');
  const years = yearParts.length === 2 ? element('p', 'ep-intro__years') : null;
  if (years) {
    years.setAttribute('aria-hidden', 'true');
    years.append(
      element('span', '', yearParts[0]),
      element('i', '', '—'),
      element('span', '', yearParts[1]),
    );
  }

  if (spec.beatId) {
    const surface = element('div', 'ep-map-stage__surface ep-intro__surface');
    section.append(surface, wash);
    if (portrait) section.append(portrait);
    if (years) section.append(years);
    section.append(copy);
  } else {
    section.append(wash);
    if (portrait) section.append(portrait);
    if (years) section.append(years);
    section.append(copy);
  }
  return section;
}

export function createChapterHeader(spec: ChapterHeaderSpec): HTMLElement {
  const header = element('header', 'ep-chapter-head');
  if (spec.photo) header.append(photoFigure(spec.photo, 'ep-chapter-head__photo'));
  else header.classList.add('is-text-only');
  const copy = element('div', 'ep-chapter-head__copy');
  const meta = element('p', 'ep-chapter-head__meta', `${spec.index} · ${spec.era}`);
  copy.append(meta, element('h2', '', spec.title), element('p', 'ep-chapter-head__lead', spec.lead));
  header.append(copy);
  return header;
}

export function createMapStage(spec: ChapterHeaderSpec & {
  beatId: string;
  placeLabel: string;
  mapNote?: string;
  anchorPhoto?: PhotoSpec;
  photoStitch?: readonly MapPhotoStitch[];
}): HTMLElement {
  const section = element('section', 'ep-map-stage');
  section.dataset.beatId = spec.beatId;
  const surface = element('div', 'ep-map-stage__surface');
  if (spec.mapNote) surface.append(element('p', 'ep-map-stage__note', spec.mapNote));
  if (spec.anchorPhoto) {
    const wash = element('div', 'ep-map-stage__wash');
    wash.setAttribute('aria-hidden', 'true');
    surface.append(wash, photoFigure(spec.anchorPhoto, 'ep-map-stage__anchor'));
  }
  if (spec.photoStitch?.length) {
    const chronology = element('aside', 'ep-map-stage__chronology');
    chronology.setAttribute('aria-label', localeText('Архивные кадры по ходу маршрута', 'Жол боюндагы архивдик кадрлар'));
    chronology.append(element('p', 'ep-map-stage__chronology-kicker', localeText('Архивные кадры', 'Архивдик кадрлар')));
    const list = element('ol', 'ep-map-stage__chronology-list');
    for (const stop of spec.photoStitch) {
      const item = element('li', 'ep-map-stage__chronology-stop');
      item.append(
        photoFigure(stop.photo, 'ep-map-stage__stitch-photo'),
        element('span', 'ep-map-stage__chronology-date', stop.date),
        element('span', 'ep-map-stage__chronology-place', stop.place),
      );
      list.append(item);
    }
    chronology.append(list);
    surface.append(chronology);
  }
  const copy = element('div', 'ep-map-stage__copy');
  copy.append(
    element('p', 'ep-map-stage__place', spec.placeLabel),
    element('p', 'ep-map-stage__meta', `${spec.index} · ${spec.era}`),
    element('h2', '', spec.title),
    element('p', 'ep-map-stage__lead', spec.lead),
  );
  section.append(surface, copy);
  return section;
}

export function createProseSpread(spec: {
  eyebrow?: string;
  title?: string;
  paragraphs: string[];
  photo?: PhotoSpec;
  reverse?: boolean;
}): HTMLElement {
  const section = element('section', 'ep-prose-spread');
  if (spec.reverse) section.classList.add('is-reverse');
  const copy = element('article', 'ep-prose-spread__copy');
  if (spec.eyebrow) copy.append(element('p', 'ep-eyebrow', spec.eyebrow));
  if (spec.title) copy.append(element('h3', '', spec.title));
  for (const paragraph of spec.paragraphs) copy.append(element('p', '', paragraph));
  if (spec.photo) section.append(photoFigure(spec.photo, 'ep-photo--inline', true), copy);
  else section.append(copy);
  return section;
}

export function createEditorialColumns(spec: EditorialColumnsSpec): HTMLElement {
  const section = element('section', 'ep-editorial-columns');
  if (spec.eyebrow || spec.title || spec.lead) {
    const header = element('header', 'ep-editorial-columns__header');
    if (spec.eyebrow) header.append(element('p', 'ep-eyebrow', spec.eyebrow));
    if (spec.title) header.append(element('h3', '', spec.title));
    if (spec.lead) header.append(element('p', 'ep-editorial-columns__lead', spec.lead));
    section.append(header);
  }
  if (spec.photo) section.append(photoFigure(spec.photo, 'ep-photo--editorial-lead', true));
  const copy = element('article', 'ep-editorial-columns__copy');
  for (const paragraph of spec.paragraphs) copy.append(element('p', '', paragraph));
  section.append(copy);
  return section;
}

export function createPortraitPair(spec: PortraitPairSpec): HTMLElement {
  const section = element('section', 'ep-portrait-pair');
  const header = element('header', 'ep-portrait-pair__header');
  header.append(
    element('p', 'ep-eyebrow', spec.eyebrow),
    element('h3', '', spec.title),
    element('p', '', spec.lead),
  );
  const list = element('div', 'ep-portrait-pair__list');
  for (const person of spec.people) {
    const item = element('article', 'ep-portrait-pair__person');
    const copy = element('div', 'ep-portrait-pair__copy');
    copy.append(
      element('p', 'ep-portrait-pair__relation', person.relation),
      element('h4', '', person.name),
      element('p', '', person.text),
    );
    item.append(photoFigure(person.photo, 'ep-photo--portrait-pair', true), copy);
    list.append(item);
  }
  const body = element('div', 'ep-portrait-pair__body');
  if (spec.familyPhoto) body.append(photoFigure(spec.familyPhoto, 'ep-photo--portrait-pair-family', true));
  body.append(list);
  section.append(header, body);
  return section;
}

export function createTimeline(title: string, rows: TimelineRow[]): HTMLElement {
  const section = element('section', 'ep-timeline');
  section.append(element('h3', '', title));
  const list = element('ol', 'ep-timeline__list');
  for (const row of rows) {
    const item = element('li', 'ep-timeline__row');
    item.append(
      element('time', '', row.year),
      element('strong', '', row.title),
      element('p', '', row.detail),
    );
    list.append(item);
  }
  section.append(list);
  return section;
}

export function createMeasureField(spec: MeasureFieldSpec): HTMLElement {
  const section = element('section', `ep-measure-field ep-measure-field--${spec.tone ?? 'sage'}`);
  const measure = element('p', 'ep-measure-field__value');
  measure.append(element('strong', '', spec.value), element('span', '', spec.unit));
  const copy = element('div', 'ep-measure-field__copy');
  copy.append(element('p', 'ep-eyebrow', spec.kicker), element('p', '', spec.text));
  section.append(measure, copy);
  return section;
}

export function createDocumentFolio(spec: DocumentFolioSpec): HTMLElement {
  const section = element('aside', 'ep-document-folio');
  section.append(element('span', 'ep-document-folio__mark', spec.mark));

  if (spec.covers?.length) {
    section.classList.add('has-covers');
    const covers = element('div', 'ep-document-folio__covers');
    for (const cover of spec.covers) {
      const figure = element('figure', 'ep-document-folio__cover');
      const source = element('a');
      source.href = cover.sourceUrl;
      source.target = '_blank';
      source.rel = 'noreferrer';
      source.setAttribute('aria-label', localeText(`${cover.alt}. Открыть источник`, `${cover.alt}. Булакты ачуу`));
      const image = element('img');
      image.src = cover.src;
      image.width = cover.width;
      image.height = cover.height;
      image.alt = cover.alt;
      image.loading = 'lazy';
      image.decoding = 'async';
      source.append(image);
      const caption = element('figcaption');
      caption.append(
        element('strong', '', cover.volume),
        element('span', '', localeText(`Обложка · ${cover.sourceLabel}`, `Мукаба · ${cover.sourceLabel}`)),
      );
      figure.append(source, caption);
      covers.append(figure);
    }
    section.append(covers);
  }

  const copy = element('div', 'ep-document-folio__copy');
  copy.append(element('p', 'ep-eyebrow', spec.kicker), element('h3', '', spec.title));
  const details = element('dl');
  for (const [term, value] of [[localeText('Место издания', 'Басылган жери'), spec.place], [localeText('Год', 'Жылы'), spec.year]]) {
    details.append(element('dt', '', term), element('dd', '', value));
  }
  copy.append(details, element('p', 'ep-document-folio__note', spec.note));
  section.append(copy);
  return section;
}

export function createPlaceDossier(title: string, lead: string, items: PlaceDossierItem[]): HTMLElement {
  const section = element('section', 'ep-place-dossier');
  const header = element('header', 'ep-place-dossier__header');
  header.append(element('p', 'ep-eyebrow', localeText('Одна точка · три слоя памяти', 'Бир жер · элестин үч катмары')), element('h3', '', title), element('p', '', lead));
  const list = element('ol', 'ep-place-dossier__list');
  for (const [index, item] of items.entries()) {
    const row = element('li', 'ep-place-dossier__item');
    row.append(
      element('span', 'ep-place-dossier__index', String(index + 1).padStart(2, '0')),
      element('h4', '', item.title),
      element('p', '', item.text),
      element('small', '', item.status),
    );
    list.append(row);
  }
  section.append(header, list);
  return section;
}

export function createFactTable(title: string, rows: FactRow[]): HTMLElement {
  const section = element('section', 'ep-facts');
  section.append(element('h3', '', title));
  const table = element('table');
  const body = element('tbody');
  for (const row of rows) {
    const record = element('tr');
    const heading = element('th', '', row.label);
    heading.scope = 'row';
    const value = element('td');
    value.append(element('strong', '', row.value));
    if (row.note) value.append(element('small', '', row.note));
    record.append(heading, value);
    body.append(record);
  }
  table.append(body);
  section.append(table);
  return section;
}

export function createPhotoMosaic(title: string, photos: PhotoSpec[]): HTMLElement {
  const section = element('section', 'ep-photo-mosaic');
  const header = element('header');
  header.append(element('p', 'ep-eyebrow', localeText('Фотографии', 'Сүрөттөр')), element('h3', '', title));
  const grid = element('div', 'ep-photo-mosaic__grid');
  photos.forEach((photo, index) => grid.append(photoFigure(photo, `ep-photo--mosaic ep-photo--mosaic-${index % 5}`)));
  section.append(header, grid);
  return section;
}

export function createPhotoSequence(title: string, photos: Array<PhotoSpec & { text: string }>): HTMLElement {
  const section = element('section', 'ep-photo-sequence');
  section.append(element('h3', '', title));
  for (const [index, photo] of photos.entries()) {
    const frame = element('article', 'ep-photo-sequence__frame');
    if (index % 2) frame.classList.add('is-reverse');
    frame.append(photoFigure(photo, 'ep-photo--sequence'), element('p', '', photo.text));
    section.append(frame);
  }
  return section;
}

export function createStoryGallery(spec: {
  id?: string;
  eyebrow?: string;
  title: string;
  lead?: string;
  items: StoryGalleryItem[];
  defaultIndex?: number;
  variant?: 'feature' | 'narrative' | 'film';
}): HTMLElement {
  const variant = spec.variant ?? 'narrative';
  const section = element('section', `ep-story-gallery ep-story-gallery--${variant}`);
  if (spec.id) section.id = spec.id;

  const header = element('header', 'ep-story-gallery__header');
  if (spec.eyebrow) header.append(element('p', 'ep-eyebrow', spec.eyebrow));
  header.append(element(variant === 'feature' ? 'h2' : 'h3', '', spec.title));
  if (spec.lead) header.append(element('p', 'ep-story-gallery__lead', spec.lead));

  const list = element('div', 'ep-story-gallery__list');
  list.setAttribute('role', 'group');
  list.setAttribute('aria-label', spec.title);
  const items: HTMLElement[] = [];
  const defaultIndex = Math.min(
    Math.max(spec.defaultIndex ?? Math.floor(spec.items.length / 2), 0),
    Math.max(spec.items.length - 1, 0),
  );
  let pinnedIndex: number | null = null;

  const activate = (index: number, reveal = false): void => {
    const activeItem = items[index];
    const wasActive = activeItem?.classList.contains('is-active') ?? false;
    for (const [itemIndex, item] of items.entries()) {
      const active = itemIndex === index;
      item.classList.toggle('is-active', active);
      item.querySelector<HTMLButtonElement>('.ep-story-gallery__select')
        ?.setAttribute('aria-pressed', String(active));
    }

    if (reveal && activeItem && matchMedia('(max-width: 700px)').matches) {
      const reducedMotion = matchMedia('(prefers-reduced-motion: reduce)').matches;
      const revealActiveItem = (): void => {
        activeItem.scrollIntoView({
          block: 'center',
          behavior: reducedMotion ? 'auto' : 'smooth',
        });
      };

      requestAnimationFrame(revealActiveItem);
      if (!reducedMotion && !wasActive) {
        const revealAfterExpansion = (event: TransitionEvent): void => {
          if (event.target !== activeItem || event.propertyName !== 'height') return;
          activeItem.removeEventListener('transitionend', revealAfterExpansion);
          revealActiveItem();
        };
        activeItem.addEventListener('transitionend', revealAfterExpansion);
      }
    }
  };

  for (const [index, item] of spec.items.entries()) {
    const figure = element('figure', 'ep-story-gallery__item');
    figure.style.setProperty('--ep-story-index', String(index));
    figure.dataset.photoKey = item.photo.key;

    const select = element('button', 'ep-story-gallery__select');
    select.type = 'button';
    select.setAttribute('aria-pressed', 'false');
    select.setAttribute(
      'aria-label',
      localeText(
        `${item.year ? `${item.year}. ` : ''}${item.title}. Выбрать карточку ${index + 1} из ${spec.items.length}`,
        `${item.year ? `${item.year}. ` : ''}${item.title}. ${index + 1} / ${spec.items.length} карточканы тандоо`,
      ),
    );

    const image = photoImage(item.photo);
    if (image) select.append(image);
    else select.append(element('span', 'ep-photo__missing', localeText(`Фотография ${item.photo.key} не найдена`, `${item.photo.key} сүрөтү табылган жок`)));

    const shade = element('span', 'ep-story-gallery__shade');
    shade.setAttribute('aria-hidden', 'true');
    const copy = element('span', 'ep-story-gallery__copy');
    if (item.year) copy.append(element('time', '', item.year));
    copy.append(
      element('strong', '', item.title),
      element('span', 'ep-story-gallery__number', String(index + 1).padStart(2, '0')),
    );
    if (item.detail) copy.append(element('span', 'ep-story-gallery__detail', item.detail));
    select.append(shade, copy);

    const actions = element('span', 'ep-story-gallery__actions');
    if (item.narrativeTarget) {
      const read = element('a', 'ep-story-gallery__read', localeText('Перейти к главе ↓', 'Бөлүмгө өтүү ↓'));
      read.href = `#${item.narrativeTarget}`;
      actions.append(read);
    }

    figure.append(select, actions);
    items.push(figure);
    list.append(figure);

    select.addEventListener('click', () => {
      pinnedIndex = index;
      activate(index, true);
    });
    select.addEventListener('focus', () => {
      if (pinnedIndex === null) activate(index);
    });
    select.addEventListener('pointerenter', (event) => {
      if (event.pointerType === 'mouse' && pinnedIndex === null) activate(index);
    });
  }

  list.addEventListener('pointerleave', (event) => {
    if (event.pointerType === 'mouse' && pinnedIndex === null) activate(defaultIndex);
  });
  section.append(header, list);
  activate(defaultIndex);
  return section;
}

export function createSourceNote(title: string, text: string, references: string): HTMLElement {
  const aside = element('aside', 'ep-source-note');
  aside.append(
    element('h3', '', title),
    element('p', '', text),
    element('small', '', references),
  );
  return aside;
}

export function createQuotePanel(quote: string, source: string, context = ''): HTMLElement {
  const figure = element('figure', 'ep-quote');
  figure.append(element('blockquote', '', `«${quote}»`));
  const caption = element('figcaption');
  caption.append(element('strong', '', source));
  if (context) caption.append(element('span', '', context));
  figure.append(caption);
  return figure;
}

export function createChapterSection(
  id: string,
  header: ChapterHeaderSpec,
  blocks: HTMLElement[],
  tone: 'paper' | 'limestone' | 'sage' | 'blue' | 'ochre' | 'clay' = 'paper',
): HTMLElement {
  const section = element('section', `ep-chapter ep-chapter--${tone}`);
  section.id = id;
  section.dataset.epChapter = id;
  section.classList.add(Number(header.index) % 2 === 0 ? 'is-even' : 'is-odd');
  section.append(createChapterHeader(header), ...blocks);
  return section;
}

export function mountSegmentSpy(root: ParentNode = document): void {
  const sections = [...root.querySelectorAll<HTMLElement>('[data-ep-chapter]')];
  const links = [...root.querySelectorAll<HTMLAnchorElement>('[data-segment-target]')];
  if (!sections.length || !links.length) return;

  const rails = [...new Set(
    links
      .map((link) => link.closest<HTMLElement>('.ep-segments'))
      .filter((rail): rail is HTMLElement => rail !== null),
  )];
  let activeId = '';
  const activate = (id: string): void => {
    if (id === activeId) return;
    activeId = id;

    for (const link of links) setSegmentLinkCurrent(link, link.dataset.segmentTarget === id);
    for (const rail of rails) updateSegmentRailState(rail, id);
  };

  const update = (): void => {
    const focus = window.innerHeight * 0.32;
    const containing = sections.find((section) => {
      const rect = section.getBoundingClientRect();
      return rect.top <= focus && rect.bottom > focus;
    });
    const nearest = containing ?? sections.reduce((best, section) => {
      const distance = Math.abs(section.getBoundingClientRect().top - focus);
      return distance < best.distance ? { section, distance } : best;
    }, { section: sections[0], distance: Number.POSITIVE_INFINITY }).section;
    if (nearest) activate(nearest.dataset.epChapter ?? '');
  };

  window.addEventListener('scroll', update, { passive: true });
  window.addEventListener('resize', update, { passive: true });
  update();
}

export function mountMapStages(root: ParentNode = document): void {
  const stages = [...root.querySelectorAll<HTMLElement>('.ep-map-stage[data-beat-id]')];
  const firstStage = stages[0];
  const firstSurface = firstStage?.querySelector<HTMLElement>('.ep-map-stage__surface');
  if (!firstStage || !firstSurface) return;

  const shell = element('div', 'ep-map-stage__map-shell');
  const canvas = element('div', 'ep-map-stage__map');
  const status = element('p', 'ep-map-stage__status', localeText('Карта загружается', 'Карта жүктөлүүдө'));
  shell.append(canvas, status);
  firstSurface.append(shell);

  let loading = false;
  const mount = async (): Promise<void> => {
    if (loading) return;
    loading = true;

    try {
      // Intentional lazy boundary: a static import would load MapLibre before any map enters the viewport.
      const { AtlasMapController } = await import('../map/map-controller');
      const controller = new AtlasMapController(canvas, status, () => undefined);
      let activeStage: HTMLElement | null = null;

      const activate = (stage: HTMLElement): void => {
        if (stage === activeStage) return;
        const beatId = stage.dataset.beatId;
        const beat = beatId ? beatById[beatId] : undefined;
        const surface = stage.querySelector<HTMLElement>('.ep-map-stage__surface');
        if (!beat || !surface) return;
        activeStage = stage;
        surface.prepend(shell);
        requestAnimationFrame(() => {
          if (activeStage !== stage) return;
          controller.resize();
          controller.renderStory(beat);
        });
      };

      const update = (): void => {
        const focus = window.innerHeight * 0.4;
        const containing = stages.find((stage) => {
          const rect = stage.getBoundingClientRect();
          return rect.top <= focus && rect.bottom > focus;
        });
        const nearest = containing ?? stages.reduce((best, stage) => {
          const distance = Math.abs(stage.getBoundingClientRect().top - focus);
          return distance < best.distance ? { stage, distance } : best;
        }, { stage: firstStage, distance: Number.POSITIVE_INFINITY }).stage;
        activate(nearest);
      };

      window.addEventListener('scroll', update, { passive: true });
      window.addEventListener('resize', update, { passive: true });
      update();
    } catch (error) {
      console.error('Map module failed to load', error);
      status.textContent = localeText('Карта недоступна', 'Карта жеткиликсиз');
      status.classList.add('is-error');
    }
  };

  if (!('IntersectionObserver' in window)) {
    void mount();
    return;
  }

  const observer = new IntersectionObserver((entries, instance) => {
    if (!entries.some((entry) => entry.isIntersecting)) return;
    instance.disconnect();
    void mount();
  }, { rootMargin: '800px 0px' });
  for (const stage of stages) observer.observe(stage);
}
